/**
 * panelDock — shared state for docking the floating editor panels (Add, Format,
 * Media) into ONE tabbed group, Illustrator-style: drag one panel's grip so the
 * pointer hovers over another panel (or an existing group) → the target shows a
 * dock outline → release to merge them into a single tabbed panel. Tearing a tab
 * out pops it back to a floating panel; a "split" button dissolves the group.
 *
 * A module singleton (like the grid store): the panels live in different
 * subtrees / coordinate systems (Add is canvas-absolute, Format/Media are
 * viewport-fixed), so a shared store they each subscribe to is simpler than a
 * provider. Hit-testing works in viewport-client coordinates — each panel (and
 * the group) registers a getter for its client rect, and a drag tests the
 * pointer against the others' rects. Desktop only.
 *
 * N-panel model: a GROUP is an ordered `members` list (2 or 3 tabs); dropping a
 * panel onto a standalone panel forms a 2-group, dropping onto a group appends,
 * tearing removes a member (the group survives until one member is left, then it
 * dissolves). `grouped` is kept in sync as `members.length >= 2`.
 */
import { useSyncExternalStore } from "react";

export type DockTab = "add" | "format" | "media";
/** A drag can land on a standalone panel OR the existing group. */
export type DropTargetId = DockTab | "group";

/** All dockable panels, in a stable order (used to scan hit-test targets). */
export const PANELS: readonly DockTab[] = ["add", "format", "media"];

/** Human labels for the tabs — canonical so the group, the drag ghost, and the
 *  dock drop-preview all agree. */
export const TAB_LABEL: Record<DockTab, string> = {
  add: "Add",
  format: "Format",
  media: "Media",
};

interface DockState {
  /** True while ≥2 panels are merged into one tabbed group (≡ members.length ≥ 2). */
  readonly grouped: boolean;
  /** The group's tabs, left-to-right. Empty when not grouped. */
  readonly members: readonly DockTab[];
  /** Which tab's body is visible in the group. */
  readonly activeTab: DockTab;
  /** Viewport (fixed) position of the group host. */
  readonly groupPos: { readonly x: number; readonly y: number };
  /** Width (px) of the panel the group first formed over — the group animates
   *  its width from here to GROUP_WIDTH when it appears. */
  readonly groupFromWidth: number;
  /** While dragging, the panel/group under the pointer to dock onto (drives the
   *  dock drop-preview). Null when not a drop target. */
  readonly candidate: DropTargetId | null;
  /** Which HALF of the candidate the pointer is over — picks the slot the
   *  incoming tab drops into (left → incoming tab on the left). */
  readonly candidateSide: "left" | "right" | null;
  /** The panel currently being dragged (so a candidate knows what's landing on
   *  it). Null when no dock drag is in flight. */
  readonly dragSource: DockTab | null;
  /** While a tab is being dragged OUT of the group to tear it off, which tab
   *  (drives a live drag ghost). Null when not tearing. */
  readonly tearing: DockTab | null;
  /** Pointer position during a tear, for the ghost. */
  readonly tearPoint: { readonly x: number; readonly y: number };
  /** Set on tear-off release: the panel to pop out and the viewport point to
   *  drop it at. The standalone panel consumes this once it re-appears. */
  readonly tearRestore: {
    readonly tab: DockTab;
    readonly x: number;
    readonly y: number;
  } | null;
  /** Which standalone panel is on top (raised z-index) — the last interacted. */
  readonly front: DockTab;
  /** Group top-left (viewport) captured at ungroup, so a panel returning from
   *  the group can glide FROM there to its home spot. Bumped each ungroup (via
   *  `seq`) so a panel's animation effect re-fires. */
  readonly ungroupFrom: { readonly x: number; readonly y: number } | null;
  readonly ungroupSeq: number;
}

let state: DockState = {
  grouped: false,
  members: [],
  activeTab: "format",
  groupPos: { x: 0, y: 0 },
  groupFromWidth: 264,
  candidate: null,
  candidateSide: null,
  dragSource: null,
  tearing: null,
  tearPoint: { x: 0, y: 0 },
  tearRestore: null,
  front: "format",
  ungroupFrom: null,
  ungroupSeq: 0,
};

const listeners = new Set<() => void>();
const rects: Partial<Record<DropTargetId, () => DOMRect | null>> = {};
// Live group top-left, updated by the group panel (non-reactive — only snapshot
// into state at ungroup time, so per-frame group drags don't churn subscribers).
let lastGroupOrigin = { x: 0, y: 0 };

function emit(): void {
  for (const l of listeners) l();
}

function set(patch: Partial<DockState>): void {
  state = { ...state, ...patch };
  emit();
}

/** Does rect r contain the point (x,y)? */
function inside(r: DOMRect | null | undefined, x: number, y: number): boolean {
  return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

function sideOf(r: DOMRect, x: number): "left" | "right" {
  return x < r.left + r.width / 2 ? "left" : "right";
}

export const panelDock = {
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  get(): DockState {
    return state;
  },

  /** A panel (or the group, id "group") registers a getter for its live client
   *  rect so a drag can hit-test against it. Returns an unregister. */
  registerRect(id: DropTargetId, getRect: () => DOMRect | null): () => void {
    rects[id] = getRect;
    return () => {
      if (rects[id] === getRect) delete rects[id];
    };
  },

  /** During a drag of `self`, mark the panel/group under the pointer as a dock
   *  candidate. Tests the existing group first, then every OTHER standalone
   *  panel. Returns whether it hit. No-op if `self` is already in the group. */
  hitTest(self: DockTab, x: number, y: number): boolean {
    if (state.members.includes(self)) return false;
    let cand: DropTargetId | null = null;
    let side: "left" | "right" | null = null;
    // The group is the widest, most likely target — test it first.
    if (state.grouped) {
      const gr = rects["group"]?.();
      if (inside(gr, x, y)) {
        cand = "group";
        side = sideOf(gr!, x);
      }
    }
    if (!cand) {
      for (const t of PANELS) {
        if (t === self || state.members.includes(t)) continue;
        const r = rects[t]?.();
        if (inside(r, x, y)) {
          cand = t;
          side = sideOf(r!, x);
          break;
        }
      }
    }
    if (
      cand !== state.candidate ||
      side !== state.candidateSide ||
      state.dragSource !== self
    ) {
      set({ candidate: cand, candidateSide: side, dragSource: self });
    }
    return cand !== null;
  },

  /** Release of a `self` drag: if there's a candidate, merge. Dropping onto the
   *  GROUP inserts `self` as a new tab; dropping onto a standalone panel forms a
   *  new 2-tab group over it. `self` becomes the active tab. Returns whether it
   *  grouped. */
  drop(self: DockTab): boolean {
    const cand = state.candidate;
    const side = state.candidateSide ?? "right";
    if (!cand) {
      if (state.dragSource || state.candidateSide)
        set({ candidate: null, candidateSide: null, dragSource: null });
      return false;
    }
    if (cand === "group") {
      const members =
        side === "left" ? [self, ...state.members] : [...state.members, self];
      set({
        grouped: true,
        members,
        activeTab: self,
        candidate: null,
        candidateSide: null,
        dragSource: null,
      });
      return true;
    }
    // Standalone target → a fresh 2-group placed over it.
    const target = cand;
    const r = rects[target]?.();
    const members: DockTab[] =
      side === "left" ? [self, target] : [target, self];
    set({
      grouped: true,
      members,
      activeTab: self,
      groupPos: r ? { x: r.left, y: r.top } : state.groupPos,
      groupFromWidth: r ? r.width : 264,
      candidate: null,
      candidateSide: null,
      dragSource: null,
    });
    return true;
  },

  /** Raise a panel above the others (call on pointerdown within it). */
  bringToFront(tab: DockTab): void {
    if (tab !== state.front) set({ front: tab });
  },

  setActiveTab(tab: DockTab): void {
    if (tab !== state.activeTab) set({ activeTab: tab });
  },
  setGroupPos(pos: { x: number; y: number }): void {
    set({ groupPos: pos });
  },
  /** The group reports its live top-left so ungroup can snapshot it as the glide
   *  origin. Non-reactive (no re-render). */
  setGroupOrigin(x: number, y: number): void {
    lastGroupOrigin = { x, y };
  },
  /** Dissolve the group back into separate floating panels (split button). Every
   *  member returns to its pre-group position and glides there from the group. */
  ungroup(): void {
    set({
      grouped: false,
      members: [],
      candidate: null,
      ungroupFrom: lastGroupOrigin,
      ungroupSeq: state.ungroupSeq + 1,
    });
  },

  /** Mark/clear a live tab tear-off drag (drives the ghost). */
  setTearing(tab: DockTab | null): void {
    if (tab !== state.tearing) set({ tearing: tab });
  },
  setTearPoint(x: number, y: number): void {
    set({ tearPoint: { x, y } });
  },
  /** Release of a tear-off drag: pop `tab` out of the group at the drop point.
   *  If ≥2 members remain the group SURVIVES in place (only the torn tab pops
   *  out); if 1 remains the group DISSOLVES (the survivor glides home). */
  tearOff(tab: DockTab, x: number, y: number): void {
    const rest = state.members.filter((m) => m !== tab);
    if (rest.length >= 2) {
      set({
        members: rest,
        activeTab: state.activeTab === tab ? rest[0] : state.activeTab,
        candidate: null,
        tearing: null,
        dragSource: null,
        tearRestore: { tab, x, y },
      });
      return;
    }
    // Down to one → dissolve; the lone survivor glides home (ungroupSeq), the
    // torn tab restores at the drop point (tearRestore).
    set({
      grouped: false,
      members: [],
      candidate: null,
      tearing: null,
      dragSource: null,
      tearRestore: { tab, x, y },
      ungroupFrom: lastGroupOrigin,
      ungroupSeq: state.ungroupSeq + 1,
    });
  },
  /** A standalone panel claims its pending tear-off drop point (once). Returns
   *  the viewport point, or null if this panel wasn't the one torn off. */
  consumeTearRestore(tab: DockTab): { x: number; y: number } | null {
    const r = state.tearRestore;
    if (r && r.tab === tab) {
      set({ tearRestore: null });
      return { x: r.x, y: r.y };
    }
    return null;
  },
} as const;

/** Subscribe a component to the dock state. */
export function usePanelDock(): DockState {
  return useSyncExternalStore(
    panelDock.subscribe,
    panelDock.get,
    panelDock.get,
  );
}
